import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/apiClient";
import { useAuth } from "../../state/AuthContext";
import { ReasonModal } from "../ui/ReasonModal";

/**
 * G-127 (owner, 2026-10-03) — delete an uploaded supplier paper so the same
 * PDF can be uploaded and read again (the identical file is otherwise refused
 * as a copy, OD-144). Owner and admin only; hidden once the paper became a
 * delivery. The server makes the same checks and also refuses a paper with
 * payments or one still being read.
 */
export function DeletePaperButton({
  invoiceId,
  label,
  status,
  hasDelivery,
}: {
  invoiceId: string;
  label: string;
  status: string;
  hasDelivery: boolean;
}) {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasRole("OWNER", "ADMIN") || hasDelivery || status === "COMMITTED") return null;

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteInvoice(invoiceId);
      navigate("/app/invoices/upload", { state: { deletedPaper: label } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not delete the paper");
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="button danger" onClick={() => setOpen(true)}>
        Delete paper
      </button>
      {open ? (
        <ReasonModal
          title={`Delete ${label}?`}
          message={
            "The PDF, its lines and its items still waiting in Catalog staging are deleted, so you can " +
            "upload the same file again. Items already created from it stay. This cannot be undone."
          }
          confirmLabel={busy ? "Deleting…" : "Delete paper"}
          destructive
          busy={busy}
          error={error}
          onConfirm={() => void remove()}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
